import ApisServiceApp from './ApisServiceApp'
import { IUser, IUserCreate, IUserUpdate } from '@/interfaces/userInterfaces'
import { TypeId } from '@/interfaces/commonInterfaces'
import { apiRequest, parseListQuery } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse } from '@/utils/apiResponseUtils'
import type { ParsedQuery } from 'query-string'
import { hoTenFromHoSo } from '@/utils/hoTenUtils'

export interface INhanVienOption {
  id: number
  username: string
  hoTen?: string | null
  avatar?: string | null
}

const USERS = '/users'

const splitFullName = (fullName?: string): { firstName: string; lastName: string } => {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') }
}

const convertCreateBody = (data: IUserCreate) => ({
  username: data.username,
  password: data.password as string,
  role: data.role,
  isActive: (data as { isActive?: boolean }).isActive ?? true,
  department: data.department || undefined,
  position: data.position || undefined,
})

const buildCapNhatNguoiDungPayload = (data: IUserUpdate & { password?: string }): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  if (data.role !== undefined) out.role = data.role
  if (data.isActive !== undefined) out.isActive = data.isActive
  if (data.department !== undefined) out.department = data.department
  if (data.position !== undefined) out.position = data.position
  const pwd = data.password
  if (pwd != null && String(pwd).trim().length >= 8) out.password = String(pwd).trim()
  return out
}

const buildHoSoPayload = (data: IUserCreate | (IUserUpdate & { password?: string })) => {
  const { firstName, lastName } = splitFullName(data.fullName)
  return {
    firstName,
    lastName,
    email: data.email?.trim() || undefined,
    phoneNumber: data.phoneNumber || undefined,
    address: data.address?.trim() || undefined,
  }
}

const firstString = (q: ParsedQuery<string>, key: string): string | undefined => {
  const v = q[key]
  if (v == null) return undefined
  if (Array.isArray(v)) return v[0] != null ? String(v[0]) : undefined
  return String(v)
}

/** Query cho `GET /users/danh-sach-nguoi-dung` — `searchQuery` (URL FE) → `search` (API). */
export function buildUserListParams(q: ParsedQuery<string>): Record<string, string> {
  const out: Record<string, string> = {}
  const put = (apiKey: string, ...sourceKeys: string[]) => {
    for (const sk of sourceKeys) {
      const v = firstString(q, sk)
      if (v != null && v.trim() !== '') {
        out[apiKey] = v.trim()
        return
      }
    }
  }
  put('page', 'page')
  put('pageSize', 'pageSize')
  put('search', 'search', 'searchQuery')
  put('username', 'username')
  put('fullName', 'fullName')
  put('department', 'department')
  put('sortField', 'sortField')
  put('sortDirection', 'sortDirection')
  put('maChucNang', 'maChucNang')
  put('duAnId', 'duAnId')
  put('chiNhanhId', 'chiNhanhId')
  const ia = firstString(q, 'isActive')
  if (ia != null && ia !== '') out.isActive = ia
  return out
}

const mergeUserDetailForFe = (res: unknown): unknown => {
  if (!res || typeof res !== 'object') return res
  const body = res as Record<string, unknown>
  const user = body.data as Record<string, unknown> | undefined
  if (!user || typeof user !== 'object') return res
  // BE trả flat (ho-so-ca-nhan) hoặc nested profile/hoSo/userProfile
  const nested =
    (user.profile as Record<string, unknown> | undefined) ??
    (user.hoSo as Record<string, unknown> | undefined) ??
    (user.userProfile as Record<string, unknown> | undefined)
  const prof = nested && typeof nested === 'object' ? nested : user
  const first = String(prof.firstName ?? user.firstName ?? '')
  const last = String(prof.lastName ?? user.lastName ?? '')
  const fullName = [first, last].filter(Boolean).join(' ').trim()
  return {
    ...body,
    data: {
      ...user,
      profile: {
        ...prof,
        firstName: first || undefined,
        lastName: last || undefined,
        email: prof.email ?? user.email ?? null,
        phoneNumber: prof.phoneNumber ?? prof.phone ?? user.phoneNumber ?? null,
        address: prof.address ?? user.address ?? null,
        avatar: prof.avatar ?? user.avatar ?? null,
      },
      fullName: fullName || user.fullName,
      email: prof.email ?? user.email,
      phoneNumber: prof.phoneNumber ?? prof.phone ?? user.phoneNumber,
      address: prof.address ?? user.address,
      avatar: prof.avatar ?? user.avatar ?? null,
    },
  }
}

/**
 * `POST /users/tao-nguoi-dung` — body gộp tài khoản + hồ sơ (khớp BE; nếu thiếu field, chỉnh theo `CreateUserDto`).
 */
export function createUser(data: IUserCreate) {
  const { firstName, lastName } = splitFullName(data.fullName)
  const bodyData = {
    ...convertCreateBody(data),
    firstName,
    lastName,
    email: data.email?.trim() || undefined,
    phoneNumber: data.phoneNumber || undefined,
    address: data.address?.trim() || undefined,
  }
  return apiRequest(ApisServiceApp.post(`${USERS}/tao-nguoi-dung`, bodyData))
}

export function updateUserPassword(id: TypeId, password: string) {
  const uid = typeof id === 'string' ? parseInt(id, 10) : Number(id)
  const pwd = String(password).trim()
  return apiRequest(
    ApisServiceApp.put(`${USERS}/${uid}/cap-nhat-nguoi-dung`, {
      password: pwd,
    }),
  )
}

export function updateUser(id: TypeId, data: IUserUpdate & { password?: string }) {
  const uid = typeof id === 'string' ? parseInt(id, 10) : Number(id)
  const userPayload = buildCapNhatNguoiDungPayload(data)
  const hoSo = buildHoSoPayload(data)

  return apiRequest(
    (async () => {
      if (Object.keys(userPayload).length > 0) {
        await ApisServiceApp.put(`${USERS}/${uid}/cap-nhat-nguoi-dung`, userPayload)
      }
      await ApisServiceApp.put(`${USERS}/${uid}/cap-nhat-ho-so-nguoi-dung`, hoSo)
      return { success: true }
    })(),
  )
}

/** `GET /users/{id}/chi-tiet-nguoi-dung` — trả entity đã unwrap + flatten avatar/profile. */
export function getDetailUser(id: TypeId) {
  return apiRequest(
    ApisServiceApp.get(`${USERS}/${id}/chi-tiet-nguoi-dung`).then((res: unknown) =>
      normalizeDetailResponse(mergeUserDetailForFe(res) as { data?: unknown }),
    ),
  )
}

/** `GET /users/danh-sach-nguoi-dung` */
export function getListUser(querySt?: string) {
  const params = buildUserListParams(parseListQuery(querySt))
  return apiRequest(ApisServiceApp.get(`${USERS}/danh-sach-nguoi-dung`, { params }))
}

export function getListOptionUser(querySt?: string) {
  // Option dropdown cần toàn bộ user (không phân trang 25 mặc định) —
  // tránh value hợp lệ rơi trang sau khiến select hiển thị ID thô.
  return getListUser(querySt ?? 'pageSize=500')
}

/**
 * `GET /users/danh-sach-nhan-vien` — nhân viên active thuộc cùng dự án
 * với người dùng hiện tại (duAnId lấy từ JWT ở BE). Dùng cho Autocomplete
 * chọn nhân viên phụ trách (khách hàng, ...). Hỗ trợ `search`, `maChucNang` (tag/quyền).
 */
/** `POST /users/dong-bo-tu-auth` — kéo nhân viên đã phân quyền trên Auth về local (toàn hệ thống). */
export async function dongBoNhanVienTuAuth(): Promise<{
  tongUser: number
  taoMoi: number
  capNhat: number
  soPhanQuyen: number
}> {
  const res = await apiRequest(ApisServiceApp.post(`${USERS}/dong-bo-tu-auth`))
  const body = (res ?? {}) as unknown as {
    data?: Record<string, unknown>
  } & Record<string, unknown>
  const raw = (body.data ?? body) as Record<string, unknown>
  return {
    tongUser: Number(raw.tongUser ?? 0),
    taoMoi: Number(raw.taoMoi ?? 0),
    capNhat: Number(raw.capNhat ?? 0),
    soPhanQuyen: Number(raw.soPhanQuyen ?? 0),
  }
}

export async function fetchDanhSachNhanVien(
  search?: string,
  maChucNang?: string,
): Promise<INhanVienOption[]> {
  const params: Record<string, string> = {}
  if (search && search.trim()) params.search = search.trim()
  if (maChucNang && maChucNang.trim()) params.maChucNang = maChucNang.trim()
  const res = await apiRequest(
    ApisServiceApp.get(`${USERS}/danh-sach-nhan-vien`, { params }),
  )
  const raw = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
  const rows = (raw?.data ?? []) as Array<Record<string, unknown>>
  return rows.map((u) => {
    const hoSo = (u.hoSo as Record<string, unknown> | null | undefined) ?? null
    const hoTen = hoTenFromHoSo(
      hoSo
        ? {
            firstName: hoSo.firstName != null ? String(hoSo.firstName) : null,
            lastName: hoSo.lastName != null ? String(hoSo.lastName) : null,
          }
        : null,
    )
    const avatarRaw = u.avatar ?? hoSo?.avatar ?? null
    return {
      id: Number(u.id),
      username: String(u.username ?? ''),
      hoTen: hoTen || null,
      avatar: avatarRaw != null && String(avatarRaw).trim() ? String(avatarRaw).trim() : null,
    }
  })
}

/**
 * `GET /users/filter-users` (kho-compat) — user theo mã chức năng/tag trên
 * HTNguoiDungDuAn.role (vd WEB_BH_TAG_NHAN_VIEN_KINH_DOANH, WEB_BH_HOA_DON_ADD).
 * duAnId luôn lấy từ JWT ở BE.
 */
export async function fetchFilterUsers(params: {
  maChucNang?: string
  chiNhanhId?: number
  searchQuery?: string
  page?: number
  pageSize?: number
}): Promise<INhanVienOption[]> {
  const qp: Record<string, string | number> = { page: params.page ?? 1, pageSize: params.pageSize ?? 200 }
  if (params.maChucNang) qp.maChucNang = params.maChucNang
  if (params.chiNhanhId != null) qp.chiNhanhId = params.chiNhanhId
  if (params.searchQuery && params.searchQuery.trim()) qp.searchQuery = params.searchQuery.trim()
  const res = await apiRequest(ApisServiceApp.get(`${USERS}/filter-users`, { params: qp }))
  const body = (res ?? {}) as { results?: unknown; data?: unknown }
  const rows = (
    Array.isArray(body.results)
      ? body.results
      : Array.isArray(body.data)
        ? body.data
        : (body.data as { results?: unknown } | undefined)?.results ?? []
  ) as Array<Record<string, unknown>>
  return rows.map((u) => {
    const profile = (u.userProfile as Record<string, unknown> | null | undefined) ?? null
    // Tránh «… Mai Mai» khi firstName đã chứa tên; fallback flat fullName/hoTen
    const hoTen =
      hoTenFromHoSo(
        profile
          ? {
              firstName: profile.firstName != null ? String(profile.firstName) : null,
              lastName: profile.lastName != null ? String(profile.lastName) : null,
            }
          : null,
      ) ||
      String(u.fullName ?? u.hoTen ?? '').trim() ||
      null
    const avatarRaw = u.avatar ?? profile?.avatar ?? null
    return {
      id: Number(u.id),
      username: String(u.username ?? ''),
      hoTen,
      avatar: avatarRaw != null && String(avatarRaw).trim() ? String(avatarRaw).trim() : null,
    }
  })
}

const FILTER_LIST_TTL_MS = 60_000
const userFilterCacheByKey = new Map<string, { at: number; promise: Promise<IUser[]> }>()

/**
 * Options cho ô lọc người — không truyền `maChucNang` thì lấy list user chung.
 * Có mã (tag NVKD / quyền ADD / KTV…) → `filter-users` theo HTNguoiDungDuAn.role.
 * Cache 60s theo key mã.
 */
export function loadUserFilterOptions(maChucNang?: string): Promise<IUser[]> {
  const key = maChucNang?.trim() || ''
  const cached = userFilterCacheByKey.get(key)
  if (cached && Date.now() - cached.at < FILTER_LIST_TTL_MS) return cached.promise

  const withFullName = (u: IUser): IUser => {
    if (String(u.fullName ?? '').trim()) return u
    const nested = u.profile ?? u.hoSo ?? u.userProfile
    const first = String(nested?.firstName ?? '').trim()
    const last = String(nested?.lastName ?? '').trim()
    const fullName = [last, first].filter(Boolean).join(' ').trim()
    return fullName ? { ...u, fullName } : u
  }

  const promise = key
    ? fetchFilterUsers({ maChucNang: key, pageSize: 200 }).then((rows) =>
        rows.map(
          (u): IUser =>
            ({
              id: u.id,
              username: u.username,
              fullName: u.hoTen ?? '',
            }) as IUser,
        ),
      )
    : getListUser('page=1&pageSize=100').then((res: unknown) => {
        const raw = res as { data?: IUser[]; results?: IUser[] }
        const list = Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.results)
            ? raw.results
            : []
        return list.map(withFullName)
      })

  userFilterCacheByKey.set(key, { at: Date.now(), promise })
  promise.catch(() => {
    const cur = userFilterCacheByKey.get(key)
    if (cur?.promise === promise) userFilterCacheByKey.delete(key)
  })
  return promise
}

/** `GET /users/ho-so-ca-nhan` — trả entity đã unwrap `data`. */
export function getProfile() {
  return apiRequest(
    ApisServiceApp.get(`${USERS}/ho-so-ca-nhan`).then((res: unknown) =>
      normalizeDetailResponse(mergeUserDetailForFe(res) as { data?: unknown }),
    ),
  )
}

/** `GET /users/{id}/ho-so-nguoi-dung` */
export function getHoSoNguoiDungById(id: TypeId) {
  return apiRequest(
    ApisServiceApp.get(`${USERS}/${id}/ho-so-nguoi-dung`).then((res: unknown) =>
      normalizeDetailResponse(mergeUserDetailForFe(res) as { data?: unknown }),
    ),
  )
}

export type UpdateMyHoSoPayload = {
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  address?: string
  avatar?: string
}

/**
 * Cập nhật hồ sơ bản thân — `PUT /users/cap-nhat-ho-so-ca-nhan`
 * (BE đồng bộ họ/tên/email/SĐT/địa chỉ/avatar URL lên auth + ghi HTHoSo BH.)
 */
export function updateMyHoSo(data: UpdateMyHoSoPayload) {
  return apiRequest(ApisServiceApp.put(`${USERS}/cap-nhat-ho-so-ca-nhan`, data))
}

/**
 * Đổi mật khẩu bản thân — `POST /users/change-password`
 * (BE đổi trên auth-backend SSO trước, rồi mirror local. Sau khi đổi cần đăng nhập lại.)
 */
export function changeMyPassword(data: {
  oldPassword: string
  newPassword: string
  newPasswordRetype: string
}) {
  return apiRequest(ApisServiceApp.post(`${USERS}/change-password`, data))
}

/** `PUT /users/{id}/cap-nhat-trang-thai-nguoi-dung` — tách với cập nhật toàn bộ. */
export function updateUserStatus(id: TypeId, isActive: boolean) {
  return apiRequest(ApisServiceApp.put(`${USERS}/${id}/cap-nhat-trang-thai-nguoi-dung`, { isActive }))
}

/**
 * `GET /users/xuat-danh-sach-nguoi-dung` — file Excel; `accept-language` tuỳ chọn theo i18n.
 */
export function exportUserList(
  params?: Record<string, string | undefined>,
  acceptLanguage?: string,
) {
  return ApisServiceApp.get(`${USERS}/xuat-danh-sach-nguoi-dung`, {
    responseType: 'blob',
    params,
    headers: acceptLanguage ? { 'accept-language': acceptLanguage } : undefined,
  })
}
